/**
 * Pre-process markdown to fix common table formatting issues that cause
 * remark-gfm to fail and render tables as raw pipe-delimited text.
 *
 * Common issues fixed:
 * - Separator row with fewer columns than header row
 * - Data rows with fewer/more columns than header
 * - Missing trailing pipes on rows
 * - Missing separator row entirely (two or more consecutive pipe-rows)
 */
export function fixMarkdownTables(markdown: string): string {
  // Normalize line endings
  const lines = markdown.replace(/\r\n/g, '\n').split('\n');
  const result: string[] = [];
  let i = 0;
  let inCodeBlock = false;

  while (i < lines.length) {
    const trimmed = lines[i].trimStart();

    // Track fenced code blocks — never touch content inside them
    if (trimmed.startsWith('```') || trimmed.startsWith('~~~')) {
      inCodeBlock = !inCodeBlock;
      result.push(lines[i]);
      i++;
      continue;
    }

    if (inCodeBlock) {
      result.push(lines[i]);
      i++;
      continue;
    }

    // Detect start of a table-like block
    if (isTableRow(trimmed)) {
      const tableLines: string[] = [];

      // Collect consecutive table-like lines
      while (i < lines.length && !lines[i].trimStart().startsWith('```') && isTableRow(lines[i].trimStart())) {
        tableLines.push(lines[i]);
        i++;
      }

      // Need at least 2 lines for a table (header + separator or header + data)
      if (tableLines.length >= 2) {
        result.push(...normalizeTable(tableLines));
      } else {
        result.push(...tableLines);
      }
    } else {
      result.push(lines[i]);
      i++;
    }
  }

  return result.join('\n');
}

/** A table row starts with | and contains at least one more | */
function isTableRow(line: string): boolean {
  const t = line.trim();
  return t.startsWith('|') && t.indexOf('|', 1) !== -1;
}

/** A separator row contains only |, -, :, and whitespace */
function isSeparatorRow(line: string): boolean {
  return /^\|[\s\-:|]+(\|[\s\-:|]+)*\|?\s*$/.test(line.trim());
}

/** Count pipe-delimited cells in a row */
function countColumns(line: string): number {
  let inner = line.trim();
  if (inner.startsWith('|')) inner = inner.slice(1);
  if (inner.endsWith('|')) inner = inner.slice(0, -1);
  if (!inner) return 0;
  return inner.split('|').length;
}

/** Pad (or leave) a row so it has exactly `cols` cells, with proper pipe framing */
function padRow(line: string, cols: number, isSep: boolean): string {
  let inner = line.trim();
  if (inner.startsWith('|')) inner = inner.slice(1);
  if (inner.endsWith('|')) inner = inner.slice(0, -1);

  const cells = inner.split('|').map((c) => c.trim());

  while (cells.length < cols) {
    cells.push(isSep ? '---' : '');
  }

  // If there are too many cells, keep them — remark-gfm can handle extras
  return '| ' + cells.join(' | ') + ' |';
}

/** Normalize a collected block of table lines */
function normalizeTable(lines: string[]): string[] {
  // Determine max column count across all rows
  const maxCols = Math.max(...lines.map(countColumns), 1);
  const fixed: string[] = [];

  // Line 0 is the header
  fixed.push(padRow(lines[0], maxCols, false));

  const hasSep = lines.length > 1 && isSeparatorRow(lines[1]);

  if (hasSep) {
    // Pad existing separator to the correct column count
    fixed.push(padRow(lines[1], maxCols, true));
    for (let j = 2; j < lines.length; j++) {
      fixed.push(padRow(lines[j], maxCols, isSeparatorRow(lines[j])));
    }
  } else {
    // Insert a separator row after the header
    fixed.push('| ' + Array(maxCols).fill('---').join(' | ') + ' |');
    for (let j = 1; j < lines.length; j++) {
      fixed.push(padRow(lines[j], maxCols, isSeparatorRow(lines[j])));
    }
  }

  return fixed;
}
